import React, { useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert,
  Anchor,
  Badge,
  Breadcrumbs,
  Card,
  Center,
  Divider,
  Group,
  List,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconDatabase,
  IconFlask,
  IconTarget,
} from '@tabler/icons-react';
import { OBJECTIVES, STATUS_META } from './kpiCatalog';
import DateWindowSelector from './DateWindowSelector';

// Shared chrome for every KPI page. Each page supplies its own visuals as children; everything
// around them — the narrative, the measures, the gaps, the list of stored queries — comes from the
// catalog entry so the pages stay consistent as the exec team edits definitions.

/**
 * Categorical series colors. Sourced from the Mantine palette so they track the active color
 * scheme rather than being hardcoded hex that only reads well in light mode.
 */
export function useChartColors() {
  const theme = useMantineTheme();
  const scheme = useComputedColorScheme('light', { getInitialValueInEffect: false });
  const shade = scheme === 'dark' ? 4 : 6;
  return {
    series: [
      theme.colors.blue[shade],
      theme.colors.teal[shade],
      theme.colors.violet[shade],
      theme.colors.orange[shade],
      theme.colors.cyan[shade],
      theme.colors.grape[shade],
    ],
    positive: theme.colors.teal[shade],
    negative: theme.colors.red[shade],
    neutral: theme.colors.gray[scheme === 'dark' ? 5 : 6],
    grid: scheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3],
    axis: scheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[7],
    tooltipBg: scheme === 'dark' ? theme.colors.dark[6] : theme.white,
    tooltipBorder: scheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[3],
  };
}

/** Tooltip that inherits the current color scheme — recharts' default is hardcoded white. */
export function ChartTooltip({ active, payload, label, formatter }) {
  const c = useChartColors();
  if (!active || !payload?.length) return null;
  return (
    <Paper
      p="xs"
      radius="sm"
      style={{ background: c.tooltipBg, border: `1px solid ${c.tooltipBorder}` }}
    >
      {label != null && (
        <Text size="xs" fw={600} mb={4}>
          {label}
        </Text>
      )}
      <Stack gap={2}>
        {payload.map((entry) => (
          <Text key={entry.dataKey ?? entry.name} size="xs" c={entry.color}>
            {entry.name}: {formatter ? formatter(entry.value, entry) : entry.value}
          </Text>
        ))}
      </Stack>
    </Paper>
  );
}

/**
 * Renders loading / error / empty states around query-backed content so a failed query reads as a
 * failed query rather than as an empty chart that looks like a real zero.
 */
export function QueryState({ isLoading, error, isEmpty, children, minHeight = 200 }) {
  if (isLoading) {
    return (
      <Center h={minHeight}>
        <Loader size="sm" />
      </Center>
    );
  }
  if (error) {
    return (
      <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />} title="Query failed">
        <Text size="sm">{error.message}</Text>
      </Alert>
    );
  }
  if (isEmpty) {
    return (
      <Center h={minHeight}>
        <Text size="sm" c="dimmed">
          No rows returned for this window.
        </Text>
      </Center>
    );
  }
  return children;
}

// The reporting window in effect for the page, provided by KpiPage so any visual can stamp the
// dates it reflects without threading the window through every call site.
const WindowContext = React.createContext(null);

/**
 * A small dimmed badge stamping which dates a visual reflects. By default it shows the effective
 * reporting window (from → to) so a windowed chart carries its own range; pass `asOf` for a
 * point-in-time visual (current catalog / on-hand inventory) that can't follow the selector.
 */
export function DateTag({ asOf = false }) {
  const win = useContext(WindowContext);
  const label = asOf ? `As of ${dayjs().format('YYYY-MM-DD')}` : win ? `${win.from} → ${win.to}` : null;
  if (!label) return null;
  return (
    <Badge
      variant="light"
      color="gray"
      size="xs"
      radius="sm"
      styles={{ root: { flexShrink: 0 }, label: { textTransform: 'none', fontWeight: 500 } }}
    >
      {label}
    </Badge>
  );
}

/** A titled card to drop a chart into, with an optional "what am I looking at" caption. */
export function ChartCard({ title, caption, children, height = 280, asOf = false }) {
  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" align="flex-start" gap="xs">
          <Text fw={600}>{title}</Text>
          <DateTag asOf={asOf} />
        </Group>
        {caption && (
          <Text size="xs" c="dimmed">
            {caption}
          </Text>
        )}
        <div style={{ height, width: '100%' }}>{children}</div>
      </Stack>
    </Card>
  );
}

/** A single headline number. */
export function StatTile({ label, value, sub, color, asOf = false }) {
  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap={2}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          {label}
        </Text>
        <Title order={3} c={color}>
          {value}
        </Title>
        {sub && (
          <Text size="xs" c="dimmed">
            {sub}
          </Text>
        )}
        <Group gap={0} mt={2}>
          <DateTag asOf={asOf} />
        </Group>
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------- formatting helpers

export const fmtMoney = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${n.toFixed(0)}`;
};

export const fmtPct = (v, digits = 1) => `${(Number(v) || 0).toFixed(digits)}%`;

export const fmtInt = (v) => (Number(v) || 0).toLocaleString();

export default function KpiPage({
  kpi,
  window: reportingWindow,
  onWindowChange,
  children,
  hideMethodology = false,
}) {
  const objective = OBJECTIVES.find((o) => o.id === kpi.objectiveId);
  const status = STATUS_META[kpi.status] ?? STATUS_META.proposed;

  return (
    <Stack gap="lg" p="md">
      <Stack gap="xs">
        <Breadcrumbs separator="›">
          <Anchor component={RouterLink} to="/initiatives" size="sm">
            Initiatives
          </Anchor>
          <Text size="sm" c="dimmed">
            Objective {objective?.number}
          </Text>
          <Text size="sm">{kpi.name}</Text>
        </Breadcrumbs>

        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4}>
            <Title order={2}>{kpi.name}</Title>
            <Text c="dimmed">{kpi.tagline}</Text>
          </Stack>
          <Group gap="xs" wrap="nowrap">
            <Badge color={status.color} variant="light">
              {status.label}
            </Badge>
            <Badge color="grape" variant="light" leftSection={<IconFlask size={12} />}>
              Experimental
            </Badge>
          </Group>
        </Group>

        {onWindowChange ? (
          <DateWindowSelector value={reportingWindow} onChange={onWindowChange} />
        ) : (
          reportingWindow && (
            <Text size="xs" c="dimmed">
              Reporting window: {reportingWindow.from} to {reportingWindow.to}
            </Text>
          )
        )}
      </Stack>

      <Card withBorder radius="md" p="lg">
        <Stack gap="md">
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              The question
            </Text>
            <Text fw={500}>{kpi.question}</Text>
          </Stack>
          <Divider />
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Definition
              </Text>
              <Text size="sm">{kpi.definition}</Text>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Why it matters
              </Text>
              <Text size="sm">{kpi.whyItMatters}</Text>
            </Stack>
          </SimpleGrid>
          {kpi.executiveSummary && (
            <>
              <Divider />
              <Paper radius="md" p="md" bg="var(--mantine-color-blue-light)">
                <Stack gap={4}>
                  <Group justify="space-between" wrap="nowrap" gap="xs">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Executive summary
                    </Text>
                    <Text size="xs" c="dimmed" fs="italic">
                      Fixed view · last 3 years
                    </Text>
                  </Group>
                  <Text size="sm">{kpi.executiveSummary}</Text>
                </Stack>
              </Paper>
            </>
          )}
        </Stack>
      </Card>

      <WindowContext.Provider value={reportingWindow}>{children}</WindowContext.Provider>

      {!hideMethodology && (
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Card withBorder radius="md" p="md">
          <Stack gap="xs">
            <Group gap="xs">
              <IconTarget size={16} />
              <Text fw={600}>How we measure it</Text>
            </Group>
            <List size="sm" spacing={4}>
              {kpi.measures.map((m) => (
                <List.Item key={m}>{m}</List.Item>
              ))}
            </List>
            {kpi.targets?.length > 0 && (
              <>
                <Divider my={4} />
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Targets
                </Text>
                {kpi.targets.map((t) => (
                  <Text key={t} size="sm" c="dimmed">
                    {t}
                  </Text>
                ))}
              </>
            )}
          </Stack>
        </Card>

        <Card withBorder radius="md" p="md">
          <Stack gap="xs">
            <Text fw={600}>Data & open questions</Text>
            {kpi.dataNotes?.length > 0 && (
              <>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Where the data comes from
                </Text>
                <List size="sm" spacing={4}>
                  {kpi.dataNotes.map((n) => (
                    <List.Item key={n}>{n}</List.Item>
                  ))}
                </List>
              </>
            )}
            {/* HIDDEN 2026-07-20 — "Needs a decision" is internal working state and was
                distracting for exec readers. The gaps data is still maintained in kpiCatalog.js;
                only the rendering is switched off. Uncomment to restore. */}
            {/*
            {kpi.gaps?.length > 0 && (
              <>
                <Divider my={4} />
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Needs a decision
                </Text>
                <List size="sm" spacing={4}>
                  {kpi.gaps.map((g) => (
                    <List.Item key={g}>{g}</List.Item>
                  ))}
                </List>
              </>
            )}
            */}
          </Stack>
        </Card>
      </SimpleGrid>
      )}

      {kpi.queries?.length > 0 && (
        <Card withBorder radius="md" p="md">
          <Stack gap="xs">
            <Group gap="xs">
              <IconDatabase size={16} />
              <Text fw={600}>Stored queries behind this page</Text>
            </Group>
            <Text size="xs" c="dimmed">
              None of this SQL lives in the application. Each query below is a row in
              Data.NexusQueries and can be read, validated and edited directly — no code changes
              required.
            </Text>
            <List size="sm" spacing={6}>
              {kpi.queries.map((q) => (
                <List.Item key={q.key}>
                  <Anchor
                    component={RouterLink}
                    to={`/query-commander/editor?queryKey=${encodeURIComponent(q.key)}`}
                    size="sm"
                  >
                    <Text span ff="monospace" size="sm">
                      {q.key}
                    </Text>
                  </Anchor>
                  <Text span size="sm" c="dimmed">
                    {' '}
                    — {q.purpose}
                  </Text>
                </List.Item>
              ))}
            </List>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
